// src/components/Common/MoodleEventBooking/MoodleUsersSelect.tsx

import React, { useState, useEffect, useMemo } from 'react';
import { HbrOption, HbrSelect, HbrTag } from '@harbor/elements-react-standalone';
import { useJoomlaUserSearch, UserLike } from '../../../hooks/useMoodleEventBooking';

interface MoodleUsersSelectProps {
  multi?: boolean;
  preselected?: UserLike[];
  onChange?: (value: number | number[] | null) => void;
  label?: string;
  userId?: number | string;
  username?: string;
  email?: string;
  availableUserIds?: (number | string)[];
  availableUsernames?: string[];
  availableEmails?: string[];
}

export function MoodleUsersSelect({
  multi = false,
  preselected = [],
  onChange,
  label = 'Select user(s)',
  userId,
  username,
  email,
  availableUserIds = [],
  availableUsernames = [],
  availableEmails = [],
}: MoodleUsersSelectProps) {
  const [search, setSearch] = useState('');
  const [initialUserLoaded, setInitialUserLoaded] = useState(false);
  const [showOptions, setShowOptions] = useState(false);

  const [selectedLocal, setSelectedLocal] = useState<UserLike[]>(preselected);
  useEffect(() => {
    setSelectedLocal(preselected || []);
  }, [preselected]);

  const selectedIds = selectedLocal.map(u => u.id);

  const { data: initialUserResults = [] } = useJoomlaUserSearch(
    {
      user_ids: userId !== undefined && userId !== null && userId !== '' ? [userId] : [],
      usernames: !userId && username ? [username] : [],
      emails: !userId && !username && email ? [email] : [],
      limit: 10,
    },
    {
      enabled:
        !initialUserLoaded &&
        (
          (userId !== undefined && userId !== null && userId !== '') ||
          !!username ||
          !!email
        ),
    }
  );

  const { data: baselineResults = [] } = useJoomlaUserSearch(
    {
      user_ids: availableUserIds,
      usernames: availableUsernames,
      emails: availableEmails,
      limit: 50,
    },
    {
      enabled:
        (availableUserIds?.length || 0) > 0 ||
        (availableUsernames?.length || 0) > 0 ||
        (availableEmails?.length || 0) > 0,
    }
  );

  const { data: results = [] } = useJoomlaUserSearch({
    q: search,
    user_ids: selectedIds.length ? selectedIds : [],
    limit: 25,
  });

  useEffect(() => {
    if (!onChange) return;
    if (multi) {
      onChange(selectedLocal.map(u => u.id));
    } else {
      onChange(selectedLocal.length ? selectedLocal[0].id : null);
    }
  }, [selectedLocal, multi, onChange]);

  useEffect(() => {
    if (!initialUserLoaded && initialUserResults.length > 0) {
      if (multi) {
        const missing = initialUserResults.filter(u => !selectedLocal.find(su => su.id === u.id));
        if (missing.length) {
          setSelectedLocal(prev => [...prev, ...missing]);
        }
      } else {
        if (!selectedLocal.length || selectedLocal[0].id !== initialUserResults[0].id) {
          setSelectedLocal([initialUserResults[0]]);
        }
      }
      setInitialUserLoaded(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialUserResults, initialUserLoaded, multi]);

  const handleSelect = (user: UserLike) => {
    if (multi) {
      if (!selectedLocal.find(u => u.id === user.id)) {
        setSelectedLocal(prev => [...prev, user]);
      }
    } else {
      setSelectedLocal([user]);
    }
    setSearch('');
    setShowOptions(false);
  };

  const handleRemove = (id: number) => {
    if (multi) {
      setSelectedLocal(prev => prev.filter(u => u.id !== id));
    } else {
      setSelectedLocal([]);
    }
  };

  const options = useMemo(() => {
    const map = new Map<number | string, UserLike>();
    selectedLocal.forEach(u => map.set(u.id, u));
    (baselineResults ?? []).forEach(u => map.set(u.id, u));
    (results ?? []).forEach(u => map.set(u.id, u));
    return Array.from(map.values()).slice(0, 25);
  }, [selectedLocal, baselineResults, results]);

  return (
    <div>
      <HbrSelect
        label={label}
        value={search}
        placeholder="Search users..."
        autocomplete={true}
        onHbr-search={e => {
          const val = (e?.detail?.value ?? '').trim();
          setSearch(val);
          setShowOptions(!!val && val !== '');
        }}
      >
      {showOptions && !!options.length && options
            .filter(u => (multi ? !selectedLocal.find(sel => sel.id === u.id) : true))
            .map(user => (
              <HbrOption
                key={user.id}
                onHbr-option-click={() => handleSelect(user)}
              >
                {`${user.name}${user.email ? ` (${user.email})` : ''}`}
              </HbrOption>
            )
      )}
      </HbrSelect>

      <div className={`mb-3 ${selectedLocal.length ? 'mt-2' : ''} flex flex-wrap gap-1`}>
        {selectedLocal.map(user => (
          <HbrTag key={user.id} removable onHbr-remove={() => handleRemove(user.id)} style={{ marginRight: 4, marginBottom: 4, display: 'inline-block' }}>{user.name}</HbrTag>
        ))}
      </div>
    </div>
  );
}